import {
  useCallback,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";
import type { ProductSummary } from "@cabal/shared/domain/session/product/types.js";
import {
  LiveFlashCommonField,
  LiveFlashKind,
  TRACKED_PRODUCT_FIELD,
  type TrackedProductField,
} from "@/types/frontend-types";
import {
  selectProductMergeFlashSelection,
  type ProductMergeAncestorFlag,
  type ProductMergeFlashSelection,
  type ProductMergeOrphanChildInsert,
  type ProductMergeProductFlag,
} from "./product-merge-selectors";
import type {
  FlagLiveFlash,
  GetAncestorChain,
  IsInUserAttachmentsSubtree,
  MergeProductsAndFlagChangesInput,
} from "./session-live-signals.types";

interface UseSessionProductMergeLiveSignalsInput {
  productsRef: MutableRefObject<Record<string, ProductSummary>>;
  userAttachmentsProductIdRef: MutableRefObject<string | null>;
  setProducts: Dispatch<SetStateAction<Record<string, ProductSummary>>>;
  getAncestorChain: GetAncestorChain;
  isInUserAttachmentsSubtree: IsInUserAttachmentsSubtree;
  flagLiveFlash: FlagLiveFlash;
}

export function useSessionProductMergeLiveSignals({
  productsRef,
  userAttachmentsProductIdRef,
  setProducts,
  getAncestorChain,
  isInUserAttachmentsSubtree,
  flagLiveFlash,
}: UseSessionProductMergeLiveSignalsInput): (
  input: MergeProductsAndFlagChangesInput,
) => void {
  return useCallback(
    ({ items, canFlash }: MergeProductsAndFlagChangesInput): void => {
      const selection = selectProductMergeFlashSelection({
        items,
        productsById: productsRef.current,
        canFlash,
        userAttachmentsProductId: userAttachmentsProductIdRef.current,
      });

      setProducts((previousProducts) => mergeProductsWithOrphanChildren({
        previousProducts,
        items,
        orphanChildInserts: selection.orphanChildInserts,
      }));

      const flashSelection = selectFlashSelectionWithOrphanParents({
        selection,
        canFlash,
        getAncestorChain,
        isInUserAttachmentsSubtree,
      });
      flagProductMergeSelection({
        selection: flashSelection,
        isInUserAttachmentsSubtree,
        flagLiveFlash,
      });
    },
    [
      flagLiveFlash,
      getAncestorChain,
      isInUserAttachmentsSubtree,
      productsRef,
      setProducts,
      userAttachmentsProductIdRef,
    ],
  );
}

function mergeProductsWithOrphanChildren(input: {
  previousProducts: Record<string, ProductSummary>;
  items: readonly ProductSummary[];
  orphanChildInserts: readonly ProductMergeOrphanChildInsert[];
}): Record<string, ProductSummary> {
  const updated = { ...input.previousProducts };
  input.items.forEach((product) => {
    updated[product.id] = product;
  });
  input.orphanChildInserts.forEach(({ childId, parentId }) => {
    const parent = updated[parentId];
    if (!parent || parent.subproductIds.includes(childId)) return;
    updated[parentId] = {
      ...parent,
      subproductIds: [...parent.subproductIds, childId],
      subproductCount: parent.subproductCount + 1,
    };
  });
  return updated;
}

function selectFlashSelectionWithOrphanParents(input: {
  selection: ProductMergeFlashSelection;
  canFlash: boolean;
  getAncestorChain: GetAncestorChain;
  isInUserAttachmentsSubtree: IsInUserAttachmentsSubtree;
}): ProductMergeFlashSelection {
  const orphanFlags = selectOrphanParentFlashFlags(input);
  return {
    ...input.selection,
    productFlags: [...input.selection.productFlags, ...orphanFlags.productFlags],
    ancestorSubproductFlags: [
      ...input.selection.ancestorSubproductFlags,
      ...orphanFlags.ancestorSubproductFlags,
    ],
  };
}

function selectOrphanParentFlashFlags(input: {
  selection: ProductMergeFlashSelection;
  canFlash: boolean;
  getAncestorChain: GetAncestorChain;
  isInUserAttachmentsSubtree: IsInUserAttachmentsSubtree;
}): Pick<ProductMergeFlashSelection, "productFlags" | "ancestorSubproductFlags"> {
  if (!input.canFlash) return { productFlags: [], ancestorSubproductFlags: [] };

  const flaggedParents = new Set<string>();
  const productFlags: ProductMergeProductFlag[] = [];
  const ancestorSubproductFlags: ProductMergeAncestorFlag[] = [];
  input.selection.orphanChildInserts.forEach(({ parentId }) => {
    if (input.isInUserAttachmentsSubtree(parentId)) return;
    if (!flaggedParents.has(parentId)) {
      flaggedParents.add(parentId);
      productFlags.push({
        productId: parentId,
        fields: [TRACKED_PRODUCT_FIELD.SubproductCount],
      });
    }
    input.getAncestorChain(parentId).forEach((ancestorId) => {
      ancestorSubproductFlags.push({ ancestorId, originId: parentId });
    });
  });
  return { productFlags, ancestorSubproductFlags };
}

function flagProductMergeSelection(input: {
  selection: ProductMergeFlashSelection;
  isInUserAttachmentsSubtree: IsInUserAttachmentsSubtree;
  flagLiveFlash: FlagLiveFlash;
}): void {
  flagProductFields(input);
  flagVersionArrivals(input);
  flagFileArrivals(input);
  flagAncestorProductFields({
    flags: input.selection.ancestorSubproductFlags,
    field: TRACKED_PRODUCT_FIELD.SubproductCount,
    isInUserAttachmentsSubtree: input.isInUserAttachmentsSubtree,
    flagLiveFlash: input.flagLiveFlash,
  });
  flagAncestorProductFields({
    flags: input.selection.ancestorFileFlags,
    field: TRACKED_PRODUCT_FIELD.AttachedFiles,
    isInUserAttachmentsSubtree: input.isInUserAttachmentsSubtree,
    flagLiveFlash: input.flagLiveFlash,
  });
}

function flagProductFields(input: {
  selection: ProductMergeFlashSelection;
  flagLiveFlash: FlagLiveFlash;
}): void {
  input.selection.productFlags.forEach(({ productId, fields }) => {
    input.flagLiveFlash({
      kind: LiveFlashKind.Product,
      entityId: productId,
      fields,
      scopeProductId: productId,
    });
  });
}

function flagVersionArrivals(input: {
  selection: ProductMergeFlashSelection;
  flagLiveFlash: FlagLiveFlash;
}): void {
  input.selection.versionArrivalFlags.forEach(({ versionId, scopeProductId }) => {
    input.flagLiveFlash({
      kind: LiveFlashKind.Version,
      entityId: versionId,
      fields: [LiveFlashCommonField.Arrival],
      scopeProductId,
    });
  });
}

function flagFileArrivals(input: {
  selection: ProductMergeFlashSelection;
  flagLiveFlash: FlagLiveFlash;
}): void {
  input.selection.fileArrivalFlags.forEach(({ fileId, scopeProductId }) => {
    input.flagLiveFlash({
      kind: LiveFlashKind.File,
      entityId: fileId,
      fields: [LiveFlashCommonField.Arrival],
      scopeProductId,
    });
  });
}

function flagAncestorProductFields(input: {
  flags: readonly ProductMergeAncestorFlag[];
  field: TrackedProductField;
  isInUserAttachmentsSubtree: IsInUserAttachmentsSubtree;
  flagLiveFlash: FlagLiveFlash;
}): void {
  selectAncestorIdsOutsideUserAttachments(input).forEach((ancestorId) => {
    input.flagLiveFlash({
      kind: LiveFlashKind.Product,
      entityId: ancestorId,
      fields: [input.field],
      scopeProductId: ancestorId,
    });
  });
}

function selectAncestorIdsOutsideUserAttachments(input: {
  flags: readonly ProductMergeAncestorFlag[];
  isInUserAttachmentsSubtree: IsInUserAttachmentsSubtree;
}): string[] {
  const originByAncestorId = new Map<string, string>();
  input.flags.forEach(({ ancestorId, originId }) => {
    if (input.isInUserAttachmentsSubtree(originId)) return;
    if (!originByAncestorId.has(ancestorId)) {
      originByAncestorId.set(ancestorId, originId);
    }
  });
  return [...originByAncestorId.keys()];
}
